import { useCallback, useEffect, useRef } from 'react';

import { actions } from '@kanda-libs/ks-frontend-services';

import { useAppDispatch } from 'components/App';
import useApiError from 'hooks/useApiError';
import { useStatusContext } from 'pages/Status/context';

export interface HummPendingHook {
  link?: string;
}

export default function useHummPending(): HummPendingHook {
  const { credit } = useStatusContext();

  const dispatch = useAppDispatch();
  const onError = useApiError('Error checking credit - please refresh');

  const checkCreditAndRevalidate = useCallback(
    (id: string) => {
      dispatch(
        actions.checkCredit({
          params: { id },
          onError,
          onSuccess: () => {
            actions.getCredit({
              params: { id },
              forceReload: true,
              onError,
            });
          },
        }),
      );
    },
    [dispatch, onError],
  );

  // Reference for autoredirect timer
  const timerRef = useRef<NodeJS.Timer | undefined>();
  // Function for autoredirect
  const startTimeout = useCallback(
    (id: string) => {
      clearTimeout(timerRef.current as NodeJS.Timeout);
      timerRef.current = setInterval(() => {
        checkCreditAndRevalidate(id);
      }, 15000);
    },
    [checkCreditAndRevalidate],
  );

  // Reference for autoredirect timer
  const redirectRef = useRef<NodeJS.Timer | undefined>();
  // Function for autoredirect
  const startRedirect = useCallback((url: string) => {
    clearTimeout(redirectRef.current as NodeJS.Timeout);
    redirectRef.current = setTimeout(() => {
      window.location.href = url;
    }, 10000);
  }, []);

  const creditIsCheckedRef = useRef<boolean>(false);
  useEffect(() => {
    if (!credit || creditIsCheckedRef.current) return;
    const id = credit?.id;
    const xref = credit?.xref;
    const state = credit?.state;
    if (xref && state === 'pending') {
      creditIsCheckedRef.current = true;
      startRedirect(xref);
      return;
    }
    if (!id) return;
    creditIsCheckedRef.current = true;
    startTimeout(id);
  }, [credit, checkCreditAndRevalidate, startTimeout, startRedirect]);

  return {
    link: credit?.xref,
  };
}
